(() => {
  const config = window.WazupTrackingConfig || {};
  const seenSections = new Set();
  const seenDepths = new Set();
  const gaQueue = [];
  let gaReady = false;

  const clean = (value, fallback = 'unknown') => {
    const text = String(value || '').replace(/\s+/g, ' ').trim();
    return (text || fallback).slice(0, 100);
  };

  const pageContext = () => ({
    page_template: clean(config.template),
    page_type: clean(config.pageType),
    page_path: window.location.pathname,
    page_title: clean(document.title),
  });

  const publishToShopify = (name, detail) => {
    try {
      window.Shopify?.analytics?.publish?.(name, detail);
    } catch (error) {
      console.debug('[Wazup tracking] Shopify publish skipped', error);
    }
  };

  const sendToGa = (name, detail) => {
    if (!config.ga4Id) return;
    if (!gaReady) {
      gaQueue.push([name, detail]);
      return;
    }
    window.gtag?.('event', name, detail);
  };

  const track = (name, detail = {}) => {
    const payload = { ...pageContext(), ...detail };
    publishToShopify(name, payload);
    sendToGa(name, payload);
  };

  const analyticsAllowed = () => {
    try {
      return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
    } catch {
      return false;
    }
  };

  const enableGa = () => {
    if (gaReady || !config.ga4Id || !analyticsAllowed()) return;

    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function gtag() {
      window.dataLayer.push(arguments);
    };
    window.gtag('js', new Date());
    window.gtag('config', config.ga4Id, { send_page_view: false });

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(config.ga4Id)}`;
    document.head.appendChild(script);

    gaReady = true;
    while (gaQueue.length) {
      const [name, detail] = gaQueue.shift();
      window.gtag('event', name, detail);
    }
  };

  const sectionInfo = (element) => {
    const wrapper = element.closest('[id^="shopify-section-"]') || element;
    const heading = wrapper.querySelector('h1, h2, h3, [role="heading"]');
    const rawSectionId = wrapper.id.replace(/^shopify-section-/, '');
    const sectionType = Array.from(wrapper.classList)
      .find((className) => className.startsWith('shopify-section-'));
    const sectionIdType = rawSectionId.split('__').pop();

    return {
      section_id: clean(rawSectionId),
      section_type: clean(
        sectionType?.replace(/^shopify-section-/, '') || sectionIdType,
      ),
      section_heading: clean(heading?.textContent),
    };
  };

  const observeSections = () => {
    const sections = document.querySelectorAll('[id^="shopify-section-"]');
    if (!sections.length || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.45) return;
        const info = sectionInfo(entry.target);
        if (seenSections.has(info.section_id)) return;
        seenSections.add(info.section_id);
        track('wazup_section_view', info);
        observer.unobserve(entry.target);
      });
    }, { threshold: [0.45] });

    sections.forEach((section) => observer.observe(section));
  };

  const trackClicks = () => {
    document.addEventListener('click', (event) => {
      const target = event.target.closest('a, button, summary');
      if (!target) return;

      const info = sectionInfo(target);
      const href = target.getAttribute('href') || '';
      const label = clean(target.getAttribute('aria-label') || target.textContent || target.title);
      const detail = {
        ...info,
        element_type: target.tagName.toLowerCase(),
        element_label: label,
        destination_path: href.startsWith('/') ? href.split('?')[0] : '',
      };

      track('wazup_section_click', detail);

      if (href.includes('/products/')) {
        track('wazup_product_card_click', detail);
      }
      if (href.includes('/checkout') || target.name === 'checkout') {
        track('wazup_checkout_intent', detail);
      }
      if (/size\s*(guide|chart)|fit\s*guide/i.test(label)) {
        track('wazup_size_guide_open', detail);
      }
    }, { passive: true });
  };

  const trackForms = () => {
    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const isNewsletter = form.matches('[id*="newsletter" i], [class*="newsletter" i]')
        || form.querySelector('input[name="contact[email]"]');
      if (isNewsletter) {
        track('wazup_newsletter_submit', sectionInfo(form));
      }
    });

    document.addEventListener('change', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement || input instanceof HTMLSelectElement)) return;
      if (!input.closest('variant-selects, variant-radios, .product-form__input')) return;

      track('wazup_variant_selected', {
        ...sectionInfo(input),
        option_name: clean(input.name),
        option_value: clean(input.value),
      });
    });
  };

  const trackScrollDepth = () => {
    const thresholds = [25, 50, 75, 90];
    let scheduled = false;

    const measure = () => {
      scheduled = false;
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const depth = Math.round((window.scrollY / scrollable) * 100);

      thresholds.forEach((threshold) => {
        if (depth < threshold || seenDepths.has(threshold)) return;
        seenDepths.add(threshold);
        track('wazup_scroll_depth', { scroll_depth: threshold });
      });
    };

    window.addEventListener('scroll', () => {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(measure);
    }, { passive: true });
  };

  const trackCampaignLanding = () => {
    const params = new URLSearchParams(window.location.search);
    const source = params.get('utm_source');
    if (!source) return;

    track('wazup_campaign_landing', {
      campaign_source: clean(source),
      campaign_medium: clean(params.get('utm_medium')),
      campaign_name: clean(params.get('utm_campaign')),
      campaign_content: clean(params.get('utm_content')),
    });
  };

  const init = () => {
    enableGa();
    document.addEventListener('visitorConsentCollected', enableGa);
    observeSections();
    trackClicks();
    trackForms();
    trackScrollDepth();
    trackCampaignLanding();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
